"use client";

import React from "react";
import { Tabs, TabsList, TabsTrigger } from "../Tabs/Tabs";
import { cn } from "../../lib/utils";
import { CategoryNavigationTabsProps } from "./types";
import { CATEGORY_NAV_STYLES } from "./constants";

/**
 * Horizontal navigation component with tabs for main categories.
 * 
 * @example
 * ```tsx
 * <CategoryNavigationTabs
 *   categories={[
 *     { value: "movies", label: "Movies", href: "/movies" },
 *     { value: "events", label: "Events", href: "/events" }
 *   ]}
 *   activeCategory="movies"
 *   onValueChange={(value) => console.log(value)}
 * />
 * ```
 */
export const CategoryNavigationTabs = React.forwardRef<
  HTMLDivElement,
  CategoryNavigationTabsProps
>((props, ref) => {
  const {
    categories,
    activeCategory,
    onValueChange,
    className,
    listClassName,
    triggerClassName,
    useClientNavigation = false,
  } = props;

  /**
   * Handler for tab value changes.
   * Executes the onValueChange callback if provided.
   */
  const handleValueChange = (value: string) => {
    if (onValueChange) {
      onValueChange(value);
    }
  };

  /**
   * Renders a tab trigger, optionally wrapped in a Next.js Link.
   */
  const renderTrigger = (category: typeof categories[number]) => {
    const isActive = category.value === activeCategory;
    
    const triggerClasses = cn(
      CATEGORY_NAV_STYLES.trigger.base,
      isActive ? CATEGORY_NAV_STYLES.trigger.active : CATEGORY_NAV_STYLES.trigger.inactive,
      triggerClassName
    );

    const trigger = (
      <TabsTrigger
        key={category.value}
        value={category.value}
        disabled={category.disabled}
        className={triggerClasses}
      >
        {category.label}
      </TabsTrigger>
    );

    // If useClientNavigation is true and href exists, wrap in Link
    if (useClientNavigation && category.href && !category.disabled) {
      // Dynamic import of Next.js Link to avoid direct dependency
      // In the context of the UI package, this will be handled at runtime
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const Link = require("next/link").default;
        return (
          <Link key={category.value} href={category.href}>
            {trigger}
          </Link>
        );
      } catch {
        // If Next.js is not available, render without Link
        return trigger;
      }
    }

    // If href exists but useClientNavigation is false, use native anchor tag
    if (category.href && !category.disabled && !useClientNavigation) {
      return (
        <a key={category.value} href={category.href}>
          {trigger}
        </a>
      );
    }

    return trigger;
  };

  // Determine the number of columns based on the number of categories
  const gridColsClass = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
  }[Math.min(categories.length, 4)] || "grid-cols-4";

  return (
    <Tabs
      ref={ref}
      value={activeCategory}
      onValueChange={handleValueChange}
      className={cn(CATEGORY_NAV_STYLES.root, className)}
    >
      <TabsList className={cn(CATEGORY_NAV_STYLES.list, gridColsClass, listClassName)}>
        {categories.map((category) => renderTrigger(category))}
      </TabsList>
    </Tabs>
  );
});

CategoryNavigationTabs.displayName = "CategoryNavigationTabs";
